import { MarketFactGrid } from '~/features/shared/market-content';
import type { JobListingDetail } from '~/types/hlx/jobListingDetail';

type ShiftDetail = Partial<JobListingDetail> | null | undefined;

const DISTRIBUTIONS = [
  {
    title: 'Regular Shifts',
    emptyMessage: 'Regular shift distribution not specified.',
    rows: [
      { label: 'Weekday', fields: ['numDayShifts', 'numEveShifts', 'numNightShifts'] },
      {
        label: 'Weekend',
        fields: ['numOfWeDayShifts', 'numOfWeEveShifts', 'numOfWeNightShifts'],
      },
      { label: 'Holiday', fields: ['numHolDayShifts', 'numHolEveShifts', 'numHolNightShifts'] },
    ],
  },
  {
    title: 'On-call Shifts',
    emptyMessage: 'On-call shift distribution not specified.',
    rows: [
      { label: 'Weekday', fields: ['numDayCallShifts', 'numEveCallShifts', 'numNightCallShifts'] },
      {
        label: 'Weekend',
        fields: ['numOfWeDayCallShifts', 'numOfWeEveCallShifts', 'numOfWeNightCallShifts'],
      },
      {
        label: 'Holiday',
        fields: ['numHolDayCallShifts', 'numHolEveCallShifts', 'numHolNightCallShifts'],
      },
    ],
  },
] as const;

const shiftValue = (value: number | null | undefined) =>
  value == null ? <span aria-label="Not specified">—</span> : value.toLocaleString('en-US');

const ShiftDistribution = ({
  detail,
  distribution,
}: {
  detail: ShiftDetail;
  distribution: (typeof DISTRIBUTIONS)[number];
}) => {
  // An authored zero is different from an absent allocation. Do not infer
  // a schedule by assigning the listing's total to an unspecified bucket.
  const hasDistribution = distribution.rows.some((row) =>
    row.fields.some((field) => detail?.[field] != null)
  );

  return (
    <div className="min-w-0">
      <h4 className="mb-3 text-[1rem] font-semibold leading-6 text-content-primary">
        {distribution.title}
      </h4>
      {hasDistribution ? (
        <table className="w-full table-fixed border-collapse" aria-label={distribution.title}>
          <thead>
            <tr className="border-b border-grey-900 text-sm font-semibold leading-5 text-content-secondary">
              <th scope="col" className="w-[30%] px-1 py-2 text-left">
                Days
              </th>
              {['Day', 'Evening', 'Night'].map((label) => (
                <th key={label} scope="col" className="px-1 py-2 text-center">
                  {label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {distribution.rows.map((row) => (
              <tr key={row.label} className="border-b border-grey-900">
                <th
                  scope="row"
                  className="px-1 py-3 text-left text-sm font-semibold leading-5 text-content-secondary"
                >
                  {row.label}
                </th>
                {row.fields.map((field) => (
                  <td
                    key={field}
                    data-cy={`provider-job-shift-${field}`}
                    className="px-1 py-3 text-center text-[1rem] font-normal leading-6 text-content-primary [overflow-wrap:anywhere]"
                  >
                    {shiftValue(detail?.[field])}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p className="text-[1rem] font-normal leading-6 text-content-secondary">
          {distribution.emptyMessage}
        </p>
      )}
    </div>
  );
};

export const ProviderJobShiftDetails = ({
  detail,
}: {
  detail?: Partial<JobListingDetail> | null;
}) => (
  <section
    className="mt-6 border-b border-grey-900 pb-6 text-left"
    data-cy="provider-job-shift-details"
  >
    <h3 className="mb-4 text-xl font-bold leading-7 text-content-primary">Shift Details</h3>
    <MarketFactGrid
      density="comfortable"
      className="mb-6"
      facts={[
        { label: 'Total Shifts', value: shiftValue(detail?.numOfShifts) },
        { label: 'Hours per Shift', value: shiftValue(detail?.hoursPerShift) },
      ]}
    />
    <div className="grid grid-cols-[repeat(auto-fit,minmax(min(100%,320px),1fr))] gap-6">
      {DISTRIBUTIONS.map((distribution) => (
        <ShiftDistribution key={distribution.title} detail={detail} distribution={distribution} />
      ))}
    </div>
  </section>
);
